import { inject, Injectable } from '@angular/core';
import { ApiService } from '@core/api/angular';
import { getQuickSearchGeometry } from '@core/api/angular/operations/get-quick-search-geometry';
import type { Proj4GeoJSONFeature } from 'proj4leaflet';
import { map, Observable, of } from 'rxjs';
import { LassiProfiles, parseUrlLayers } from './layers';
import { MapEnquiryOption } from './map';
import { QuickSearchStore } from './quick-search';
import { QuickSearchResult } from './quick-search.model';

@Injectable({
  providedIn: 'root',
})
export class QuickSearchService {
  private readonly apiService = inject(ApiService);
  private readonly store = inject(QuickSearchStore);

  setQuickSearchPreset(
    profile: LassiProfiles,
    custom_preset: string | null | undefined,
  ) {
    if (profile === 'Custom') {
      if (!custom_preset) {
        return;
      }

      this.setQuickSearchCustom(
        parseUrlLayers(custom_preset)?.map((layer) => layer.name) ?? [],
      );
    } else {
      this.setQuickSearchEnquiry(profile);
    }
  }

  private setQuickSearchCustom(layers: string[]) {
    const searchTypes: string[] = [
      'address',
      'lga',
      'locality',
      'parish',
      'road',
      'township',
    ];

    layers.forEach((layer) => {
      // parcel related
      if (layer.startsWith('PARCEL')) {
        searchTypes.push('parcel');
      }

      // property
      if (layer === 'PROPERTY') {
        searchTypes.push('property');
      }

      // survey label
      if (
        [
          'BUA_PROPOSED_ANNO',
          'RETIRED_SURVEY_LABEL',
          'SURVEY_LABEL',
          'PLAN',
        ].includes(layer)
      ) {
        searchTypes.push('survey_label');
      }

      // survey mark
      if (layer.startsWith('SMES')) {
        searchTypes.push('survey_mark');
      }

      // vicnames related
      if (
        ['PLACE_NAMES', 'CUSTOM_RADIUS', 'PLM25_EXTENTS'].includes(layer) ||
        layer.startsWith('FOI_')
      ) {
        searchTypes.push('place');
      }
    });

    this.store.setSearchTypes(Array.from(new Set(searchTypes)));
  }

  private setQuickSearchEnquiry(enquiry: MapEnquiryOption) {
    switch (enquiry) {
      case MapEnquiryOption.PropertyEnquiry:
        return this.store.setSearchTypes(
          this.store.searchTypeList
            .filter((type) => type.value !== 'place')
            .map(({ value }) => value),
        );
      case MapEnquiryOption.SurveyMarkEnquiry:
        return this.store.setSearchTypes(
          this.store.searchTypeList
            .filter((type) => {
              return type.value !== 'place' && type.value !== 'property';
            })
            .map(({ value }) => value),
        );
      case MapEnquiryOption.VicnamesEnquiry:
        return this.store.setSearchTypes(
          this.store.searchTypeList
            .filter((type) => {
              const notAllowed = new Set([
                'parcel',
                'property',
                'survey_label',
                'survey_mark',
              ]);
              return !notAllowed.has(type.value);
            })
            .map(({ value }) => value),
        );
    }
  }

  public getQuickSearchGeometry(
    result: QuickSearchResult,
  ): Observable<Proj4GeoJSONFeature> {
    switch (result.type) {
      case 'survey_mark': {
        return of({
          type: 'Feature',
          geometry: {
            type: 'Point',
            coordinates:
              result.location?.split(',').map(parseFloat).reverse() ?? [],
          },
          properties: {},
        });
      }

      default:
        return this.apiService
          .invoke(getQuickSearchGeometry, {
            searchFor: result.doc_id!,
            searchType: result.type,
          })
          .pipe(map((response) => response.geometry as Proj4GeoJSONFeature));
    }
  }
}
